import { useRef, useState } from 'react';
import useScrollSpy from 'react-use-scrollspy';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { ContactForm, FormPartOrganizationContact } from '@/organization/ContactForm';
import { CoreDataForm, FormPartOrganizationCoreData } from '@/organization/CoreDataForm';
import { ConsultingStatusForm } from '@/organization/ConsultingStatusForm';
import { SidebarLayout } from '@/components/layouts/SidebarLayout';
import { useQueryOrganization } from '@/hooks/queries/useQueryOrganization';
import { Action, Organization, OrganizationsService, UpdateOrganizationDto } from '@/services/api';
import { DataTransformer } from '@/services/DataTransformer';
import { FormPartOrganizationStatus } from '@/components/form-elements/ConsultingStatusRegister';
import { QueryKeys } from '@/services/QueryKeys';
import { useInitialFormValues } from '@/hooks/useInitialFormValues';
import { useAppAbility } from '@/hooks/useAppAbility';
import { ApiValidationError } from '@/types/ApiValidationError';
import { useSetValidationErrors } from '@/hooks/useSetValidationErrors';
import { useAutoSave } from '@/hooks/useAutoSave';
import { LastSaved } from '@/components/form-elements/LastSaved';

type FormData = FormPartOrganizationCoreData & FormPartOrganizationContact & FormPartOrganizationStatus;

export const OrganizationCoreDataPage = () => {
  const { organizationId } = useParams();
  const ability = useAppAbility();

  const methods = useForm<FormData>();
  const { setValidationErrors } = useSetValidationErrors(methods.setError);
  const [lastSaved, setLastSaved] = useState<Dayjs>();

  const queryClient = useQueryClient();
  const { data: organization } = useQueryOrganization();

  useInitialFormValues<FormData>({
    entity: organization && {
      ...organization,
      participationDate: organization.participationDate ? dayjs(organization.participationDate) : null,
      consultingCaseId: organization.consultingCaseId || '',
    },
    useFormReturn: methods,
    fields: [
      // CoreData
      'name',
      'participationDate',
      'consultingCaseId',
      'size',
      'sector',
      'fileStorage',
      // Status
      'status',
      // Contact
      'contactFirstName',
      'contactLastName',
      'contactMail',
      'contactTel',
    ],
  });

  const { mutate } = useMutation<Organization, ApiValidationError<FormData>, UpdateOrganizationDto>(
    (requestBody: UpdateOrganizationDto) => OrganizationsService.update({ id: organizationId as string, requestBody }),
    {
      onMutate: () => setLastSaved(undefined),
      onSuccess: () => {
        setLastSaved(dayjs());

        return queryClient.invalidateQueries(QueryKeys.organizations.id(organizationId as string));
      },
      onError: setValidationErrors,
    },
  );

  const handleSubmit = methods.handleSubmit(
    ({ participationDate, ...data }) => {
      const transformedData = DataTransformer.toApi(
        {
          ...data,
          participationDate:
            typeof participationDate?.format === 'function'
              ? `${participationDate.format('YYYY')}-01-01`
              : (participationDate as null | undefined),
        },
        (field) => ability.can(Action.UPDATE, organization as Organization, field),
      );

      mutate(transformedData);
    },
    () => setLastSaved(undefined),
  );
  useAutoSave(handleSubmit, methods.watch);

  const sectionRefs = [useRef<HTMLElement>(null), useRef<HTMLElement>(null), useRef<HTMLElement>(null)];

  const activeItem = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -32,
    activeSectionDefault: 0,
  });

  return (
    <SidebarLayout
      type="scroll"
      activeItem={activeItem || 0}
      navigationItems={['Stammdaten', 'Beratungsprozess Status', 'Kontakt']}
      navigationElements={sectionRefs}
      buttonLabel={ability.can(Action.UPDATE, organization as Organization) ? 'Speichern' : undefined}
      actionArea={<LastSaved lastSaved={lastSaved} />}
    >
      <FormProvider {...methods}>
        <form id="main-form" onSubmit={handleSubmit}>
          <CoreDataForm ref={sectionRefs[0]} organization={organization} isLoading={!organization} />
          <ConsultingStatusForm ref={sectionRefs[1]} organization={organization} />
          <ContactForm ref={sectionRefs[2]} organization={organization} isLoading={!organization} />
        </form>
      </FormProvider>
    </SidebarLayout>
  );
};
